import React from 'react';
import { Gauge, DollarSign, Shield, Percent } from 'lucide-react';
import PocketField from './PocketField';
import { getLiquidationBufferInfo, formatR } from '../../utils/liquidationBuffer';
import { PkRow, PkPnl, PkSection, PkHero, PkTwoCol, PkMainField, PkChipRow, PkChip, PkFees, PkFee, PkCols, PkCol, PkExits } from './PocketRows';
import { fmt, fmtLvl, money } from './pocketFormat';

// Crypto market — cosmo layout. Calculations come from useCalculator (owned by
// PocketCalc); only the presentation differs.
const PocketCrypto = ({ mode = 'compact', hook }) => {
  const { values, setValues, metrics } = hook;
  const full = mode === 'full';
  const setField = (field) => (bn) => setValues((prev) => ({ ...prev, [field]: bn }));

  const pl = metrics.priceLevels || {};
  const side = (metrics.positionType || '').toLowerCase().includes('short') ? 'short'
    : (metrics.positionType || '').toLowerCase().includes('long') ? 'long' : null;

  const riskIcon = values.riskUnit === 'percent'
    ? <Percent size={12} className="text-[#6b7480]" />
    : <DollarSign size={12} className="text-[#6b7480]" />;

  const bufferInfo = getLiquidationBufferInfo({
    liquidationPrice: pl.liquidationPrice, stopLoss: values.stopLoss, entryPrice: values.entryPrice,
    requestedR: values.safetyBufferR, maxLeverage: values.maxLeverage,
  });
  const bufTone = bufferInfo?.tone === 'danger' ? 'red' : bufferInfo?.tone === 'warn' ? 'amber' : undefined;

  return (
    <div className="pk-body">
      {/* inputs — Entry | Stop, then icon-chips */}
      <PkTwoCol>
        <PkMainField label="Entry" copyValue={values.entryPrice}><PocketField value={values.entryPrice} onCommit={setField('entryPrice')} decimals={8} className="text-[12px]" /></PkMainField>
        <PkMainField label="Stop" copyValue={values.stopLoss}><PocketField value={values.stopLoss} onCommit={setField('stopLoss')} decimals={8} className="text-[12px]" /></PkMainField>
      </PkTwoCol>
      <PkChipRow>
        <PkChip icon={<Gauge size={12} className="text-[#6b7480]" />} title="Leverage"><PocketField value={values.maxLeverage} onCommit={setField('maxLeverage')} suffix="x" decimals={0} className="text-[12px]" /></PkChip>
        <PkChip icon={riskIcon} title="Risk"><PocketField value={values.risk} onCommit={setField('risk')} decimals={2} className="text-[12px]" /></PkChip>
        <PkChip label={values.multiTpEnabled ? "TPs" : "R:R"} title={values.multiTpEnabled ? "Multiple take-profits — set in Settings" : "Reward : Risk"}>{values.multiTpEnabled
          ? <span className="pk-mono text-[#eef3f8] text-[12px]">{values.tpCount}</span>
          : <PocketField value={values.nominalRR} onCommit={setField('nominalRR')} suffix="x" decimals={1} className="text-[12px]" />}</PkChip>
        <PkChip icon={<Shield size={12} className="text-[#6b7480]" />} title="Safety buffer"><PocketField value={values.safetyBufferR} onCommit={setField('safetyBufferR')} suffix="R" decimals={2} min={0.25} className="text-[12px]" /></PkChip>
      </PkChipRow>
      <PkChipRow>
        <PkChip label="Fee" title="Fee — entry / exit %">
          <PocketField value={values.entryFee} onCommit={setField('entryFee')} suffix="%" decimals={5} className="text-[12px]" />
          <span className="text-[#5b6573] text-[11px] mx-0.5">/</span>
          <PocketField value={values.exitFee} onCommit={setField('exitFee')} suffix="%" decimals={5} className="text-[12px]" />
        </PkChip>
      </PkChipRow>

      <div className="pk-sep" />

      <PkHero label="Position" side={side} value={money(metrics.positionSize)} sub={`Qty: ${fmt(metrics.quantity, { qty: true })}`} subLeft copyLabel="Position size" />
      <PkRow label="Leverage" value={`${fmt(metrics.leverage, { lev: true })}x`} sub={fmt(metrics.margin, { money: true })} subLeft copyLabel="Leverage" />
      <PkPnl profit={money(values.multiTpEnabled && metrics.multiExit ? metrics.multiExit.totalNetProfit : metrics.effectiveTake)} loss={money(metrics.effectiveStop)} />

      {values.multiTpEnabled && metrics.multiExit && <PkExits data={metrics.multiExit} mode={values.tpMode} pricePrefix="$" />}

      <PkCols>
        <PkCol>
          <PkSection label="Trade" />
          {values.multiTpEnabled && metrics.multiExit ? <>
            <PkRow sm subLeft label="R:R" value={`${fmt(metrics.multiExit.blendedNetRR)}x`} sub="net" copyLabel="Blended net R:R" />
            <PkRow sm label="Avg exit" value={`$${fmtLvl(metrics.multiExit.avgExitPrice)}`} copyLabel="Average exit" />
          </> : <>
            <PkRow sm subLeft label="R:R" value={`${fmt(metrics.optimizedRR)}x`} sub={`${fmt(metrics.nominalRR)}x`} copyLabel="Gross R:R" />
            {full && <PkRow sm subLeft label="Rookie" value={`${fmt(metrics.effectiveRR)}x`} sub={`${fmt(metrics.friction)}R`} copyLabel="Rookie R:R" />}
          </>}
          {full && bufferInfo && <PkRow sm label="Liq loss" value={`${formatR(bufferInfo.lossAtLiqR)}R`} tone={bufTone} info={bufferInfo.message} copyLabel="Loss at liquidation" />}
        </PkCol>
        <PkCol>
          <PkSection label="Levels" />
          <PkRow sm label="Liq" value={`$${fmtLvl(pl.liquidationPrice)}`} tone="red" copyLabel="Liquidation" />
          <PkRow sm label="B/E" value={`$${fmtLvl(pl.breakEven)}`} copyLabel="Break-even" />
          {!values.multiTpEnabled && <>
            <PkRow sm label="Nom TP" value={`$${fmtLvl(pl.takeProfit)}`} copyLabel="Nominal TP" />
            <PkRow sm label="Opt TP" value={`$${fmtLvl(pl.optimizedTakeProfit)}`} copyLabel="Optimized TP" />
          </>}
        </PkCol>
      </PkCols>

      {full && <>
        <PkSection label="Fees" />
        <PkFees cols={3}>
          <PkFee label="Entry" value={fmt(metrics.tradingFees?.entryFee, { money: true })} copyLabel="Entry fee" />
          <PkFee label="TP" value={fmt(metrics.tradingFees?.tpFee, { money: true })} copyLabel="TP fee" />
          <PkFee label="SL" value={fmt(metrics.tradingFees?.slFee, { money: true })} copyLabel="SL fee" />
          <span />
          <PkFee label="Tot TP" value={fmt(metrics.totalFees?.takeProfitFees, { money: true })} copyLabel="Total TP" />
          <PkFee label="Tot SL" value={fmt(metrics.totalFees?.stopLossFees, { money: true })} copyLabel="Total SL" />
        </PkFees>
      </>}
    </div>
  );
};

export default PocketCrypto;
